import { isUndefined } from "lodash";
import { createCachedSelector } from "re-reselect";
import { createSelector } from "reselect";

import { ApplicationScope } from "@superblocksteam/shared";

import type { ScopedDataTreePath } from "legacy/entities/DataTree/dataTreeFactory";
import getApiExecuteOnPageLoad from "store/slices/apisV2/utils/get-api-execute-on-pageload";
import {
  getV2ApiId,
  getV2ApiName,
} from "store/slices/apisV2/utils/getApiIdAndName";
import type { AppState } from "store/types";
import { getEntityName, pathToScopedEntityName } from "utils/dottedPaths";

import slice from "./slice";

export const selectAllV2Apis = createSelector(
  slice.selector,
  (state) => state.entities,
);

const selectApiIdToNameMap = createSelector(selectAllV2Apis, (apis) =>
  Object.fromEntries(
    Object.values(apis).map((api) => [getV2ApiId(api), getV2ApiName(api)]),
  ),
);

export const selectV2ApiById = createCachedSelector(
  slice.selector,
  (state: unknown, apiId: string | undefined) => apiId,
  (slice, apiId) => (apiId ? slice.entities[apiId] : undefined),
)((state, apiId) => apiId ?? "undefined");

export const selectAllV2ApiNames = createSelector(selectAllV2Apis, (apis) =>
  Object.values(apis).map((api) => getV2ApiName(api)),
);

export const selectV2ApiMetaById = createCachedSelector(
  slice.selector,
  (state: unknown, apiId: string | undefined) => apiId,
  (state, apiId) => (apiId ? state.meta[apiId] : undefined),
)((state: unknown, apiId: string | undefined) => apiId ?? "undefined");

export const selectV2ApiExtractedBindings = createSelector(
  slice.selector,
  selectApiIdToNameMap,
  (state, idToName) => {
    return Object.fromEntries(
      Object.entries(state.meta).map(([id, api]) => {
        return [
          id,
          {
            id,
            name: idToName[id],
            bindings: api.extractedBindings,
          },
        ];
      }),
    );
  },
);

export const selectV2ApiExtractedBindingsSingle = createCachedSelector(
  slice.selector,
  (_: unknown, apiId: string) => apiId,
  (state, apiId) => state.meta[apiId]?.extractedBindings ?? [],
)((_, apiId) => apiId);

export const selectV2ApiLoadingById = createCachedSelector(
  slice.selector,
  (state: unknown, apiId: string) => apiId,
  (state, apiId) => {
    return (
      Boolean(state.loading[apiId]) ||
      Boolean((state.meta[apiId]?.concurrentRuns ?? 0) > 0) ||
      Boolean(state.meta[apiId]?.waitingForEvaluationSince)
    );
  },
)((state, apiId) => apiId);

export const selectV2ApiMeta = createSelector(
  slice.selector,
  (state) => state.meta,
);

// TODO make sure v2 apis get loaded into the entityDependencyMap
const selectEntityDependencyMap = (state: AppState) =>
  state.legacy.evaluations.dependencies.entityDependencyMap;

const selectEntityDependentNames = createSelector(
  selectEntityDependencyMap,
  (entityDepMap) => {
    return Object.values(entityDepMap).reduce((previous, current) => {
      current.forEach((value) => previous.add(value as ScopedDataTreePath));
      return previous;
    }, new Set<ScopedDataTreePath>());
  },
);

export const selectPageLoadApis = createSelector(
  [slice.selector, selectV2ApiExtractedBindings, selectEntityDependentNames],
  (state, apiDepMap, entityDepNames) => {
    const apis = Object.values(state.entities);

    // Add Widget dependencies
    const dependentNames = new Set<string>(entityDepNames);
    // Add APIs that are explicitly called
    // Remove APIs that are explicitly not called
    apis.forEach((api) => {
      const executeOnPageLoad = getApiExecuteOnPageLoad(api?.apiPb);
      if (executeOnPageLoad === true) {
        dependentNames.add(`${ApplicationScope.PAGE}.${getV2ApiName(api)}`);
      } else if (executeOnPageLoad === false) {
        dependentNames.delete(`${ApplicationScope.PAGE}.${getV2ApiName(api)}`);
      }
    });

    // Add API dependencies based on if they are depended on by the above
    const apiBindings = Object.fromEntries(
      Object.values(apiDepMap).map((api) => [api.name, api]),
    );

    let apisToCheck = Object.values(apiBindings);
    do {
      const foundDependencies: string[] = [];
      for (const api of apisToCheck) {
        if (dependentNames.has(`${ApplicationScope.PAGE}.${api.name}`)) {
          (api.bindings ?? [])
            .map((binding) => pathToScopedEntityName(binding.dataTreePath))
            .filter((dep) => !dependentNames.has(dep))
            .filter((depName) => {
              const unscopedName = getEntityName(depName);
              // Allow if it's not an API or if the API is not turned explicitly off.
              const api = state.entities[apiBindings[unscopedName]?.id];
              return !api || getApiExecuteOnPageLoad(api?.apiPb) !== false;
            })
            .forEach((dep) => {
              dependentNames.add(dep);
              foundDependencies.push(dep);
            });
        }
      }

      apisToCheck = foundDependencies
        .map((depName) => {
          const apiName = getEntityName(depName as ScopedDataTreePath);
          return apiBindings[apiName];
        }) // Get the bindings for the dependency
        .filter(Boolean); // Filter out any that are not APIS
    } while (apisToCheck.length > 0);

    // There are three cases that can trigger an API to run on load:
    // 1. If the user has explicitly enabled this
    // 2. Without any user choice, it requires that a component -> API dependency exists
    // 3. Without any user choice, for APIB, it requires that an APIA -> APIB dependency exists and that APIA or an ancestor follows rules 1 or 2
    const result = apis.filter((api) => {
      const executeOnPageLoad = getApiExecuteOnPageLoad(api?.apiPb);
      return (
        executeOnPageLoad ||
        (dependentNames.has(
          `${ApplicationScope.PAGE}.${getV2ApiName(api)}` as const,
        ) &&
          isUndefined(executeOnPageLoad))
      );
    });
    return result;
  },
);

export const selectV2TestProfile = createCachedSelector(
  slice.selector,
  (state: unknown, apiId: string | undefined) => apiId,
  (slice, apiId) => (apiId ? slice.meta[apiId]?.testProfile : undefined),
)((state, apiId) => apiId ?? "undefined");

export const selectApiExecutionResult = createCachedSelector(
  selectV2ApiMetaById,
  (apiMeta) => apiMeta?.executionResult,
)((state, apiId) => apiId);
